import { Download, RotateCcw } from 'lucide-react';
import { motion } from 'motion/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { PageMotion } from 'src/components/layout/page-motion.tsx';
import { CONTACTS } from 'src/data/contacts.ts';
import { RESULT_TYPES } from 'src/data/result-types.ts';
import { demographicItems } from 'src/lib/demographics.ts';
import { HOVER_SPRING, SECTION_REVEAL } from 'src/lib/motion.ts';
import { toneStyles } from 'src/lib/scoring.ts';
import { buildResultPdfData } from 'src/pdf/build-result-pdf-data.ts';
import { downloadResultPdf } from 'src/pdf/download-result-pdf.tsx';
import type { Answers, Demographics, ResultType } from 'src/types/types.ts';

import { Eyebrow } from '../components/ui/eyebrow.tsx';
import type { Level } from '../components/ui/level-scores.tsx';
import { LevelScores } from '../components/ui/level-scores.tsx';
import { PrimaryButton } from '../components/ui/primary-button.tsx';
import { QuestionBreakdownSection } from '../components/ui/question-breakdown.tsx';
import { ScoreDial } from '../components/ui/score-dial.tsx';
import { StageOverview } from '../components/ui/stage-overview.tsx';

export interface ResultPageContentProps {
  overall: number;
  resultType: ResultType;
  levels: Level[];
  lead: { name: string; company: string; email: string };
  answers: Answers;
  context?: Demographics;
  onReset?: () => void;
}

/** Dezente Wiedergabe der demografischen Angaben (Institut / Größe / Position),
 * gestapelt als Fußblock in der Score-Karte. */
function DemographicsMeta({ context }: { context?: Demographics }) {
  const items = demographicItems(context);
  if (!items.length) return null;

  return (
    <dl className="mt-6 space-y-1.5 border-t border-zeb-muted pt-4 text-caption">
      {items.map((it) => (
        <div key={it.label} className="flex items-baseline justify-between gap-3">
          <dt className="text-[0.65rem] uppercase tracking-wide text-zeb-gray-400">{it.label}</dt>
          <dd className="text-right text-zeb-gray-600">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ContactAvatar({ src, name }: { src?: string; name: string }) {
  const [errored, setErrored] = useState(!src);
  const initials = name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('');

  if (!src || errored) {
    return (
      <div className="flex h-full w-full items-center justify-center text-zeb-gray-400">
        <span
          className="text-base"
          style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, letterSpacing: '-0.02em' }}>
          {initials}
        </span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      className="h-full w-full object-cover"
      onError={() => setErrored(true)}
    />
  );
}

export function ResultPageContent({
  overall,
  resultType,
  levels,
  lead,
  answers,
  context,
  onReset
}: ResultPageContentProps) {
  const [pdfBusy, setPdfBusy] = useState(false);
  const levelScoreById = Object.fromEntries(levels.map((l) => [l.id, l.score]));

  async function handleDownloadPdf() {
    if (pdfBusy) return;
    setPdfBusy(true);
    const toastId = toast.loading('PDF wird erstellt …');
    try {
      await downloadResultPdf(buildResultPdfData({ overall, lead, answers, levels, context }));
      toast.success('PDF heruntergeladen', {
        id: toastId,
        description: 'Ihre Auswertung wurde als PDF gespeichert.'
      });
    } catch (err) {
      console.error('PDF-Erzeugung fehlgeschlagen', err);
      toast.error('PDF konnte nicht erstellt werden', {
        id: toastId,
        description: 'Bitte versuchen Sie es erneut.'
      });
    } finally {
      setPdfBusy(false);
    }
  }

  return (
    <PageMotion>
      <main className="mx-auto max-w-6xl px-5 lg:px-10 py-12 lg:py-20">
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <Eyebrow>Ihre Auswertung</Eyebrow>
            <h1
              className="mt-5 text-display-sm sm:text-display-lg lg:text-hero-sm leading-[1.05] tracking-tight text-zeb-tertiary"
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 700,
                letterSpacing: '-0.02em'
              }}>
              {resultType.name}
            </h1>
            <p
              className="mt-6 max-w-xl text-body-lg leading-relaxed text-zeb-gray-700"
              style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}>
              {resultType.short}
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <PrimaryButton onClick={handleDownloadPdf}>
                <Download className="mr-2 h-4 w-4" />{' '}
                {pdfBusy ? 'PDF wird erstellt …' : 'Ergebnis als PDF herunterladen'}
              </PrimaryButton>
              {onReset && (
                <motion.button
                  onClick={onReset}
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  transition={HOVER_SPRING}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-zeb-muted bg-white px-6 py-3.5 text-zeb-tertiary shadow-zeb-xs transition-[box-shadow,background-color] hover:bg-zeb-surface hover:shadow-zeb-sm"
                  style={{ fontWeight: 500 }}>
                  <RotateCcw className="h-4 w-4" /> Check erneut starten
                </motion.button>
              )}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-lg border border-zeb-muted bg-white p-8 shadow-zeb-md">
              <ScoreDial value={overall} tone={resultType.tone} />
              <div className="mt-6 grid grid-cols-5 gap-1">
                {RESULT_TYPES.map((t) => {
                  const active = t.name === resultType.name;
                  // Vollen Namen ausgeschrieben über zwei Zeilen: „Customer" oben,
                  // Stufe darunter (statt der wenig selbsterklärenden Kürzel „C.Blind").
                  const [first, ...rest] = t.name.split(/[-\s]/);
                  return (
                    <div key={t.name} className="text-center">
                      <div
                        className={`h-1.5 rounded-full ${
                          active ? toneStyles[t.tone].soft : 'bg-zeb-muted'
                        }`}
                      />
                      <div
                        className={`mt-2 text-micro leading-tight ${
                          active ? 'text-zeb-tertiary' : 'text-zeb-gray-400'
                        }`}>
                        <span className="block">{first}</span>
                        <span className="block">{rest.join(' ')}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <DemographicsMeta context={context} />
            </div>
          </div>
        </section>

        <motion.section {...SECTION_REVEAL} className="mt-16 lg:mt-20">
          <h2
            className="text-3xl lg:text-4xl tracking-tight text-zeb-tertiary"
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              letterSpacing: '-0.02em'
            }}>
            Reifegrad entlang der drei Ebenen
          </h2>
          <p className="mt-3 max-w-2xl text-body-sm text-zeb-gray-600">
            Wo zeigt Ihre Organisation Stärke – wo sehen wir Handlungsfelder?
          </p>

          <div className="mt-10">
            <LevelScores levels={levels} />
          </div>
        </motion.section>

        <motion.section {...SECTION_REVEAL} className="mt-16 lg:mt-44">
          <h2
            className="text-3xl lg:text-4xl tracking-tight text-zeb-tertiary"
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              letterSpacing: '-0.02em'
            }}>
            Die fünf Stufen im Überblick
          </h2>

          <StageOverview reached={resultType} className="mt-8" />
        </motion.section>

        <motion.section {...SECTION_REVEAL} className="mt-16 lg:mt-20">
          <h2
            className="text-3xl lg:text-4xl tracking-tight text-zeb-tertiary"
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              letterSpacing: '-0.02em'
            }}>
            Auswertung der Einzelfragen
          </h2>

          <div className="mt-8">
            <QuestionBreakdownSection answers={answers} scores={levelScoreById} />
          </div>
        </motion.section>

        <motion.section {...SECTION_REVEAL} className="mt-16 lg:mt-20">
          <Eyebrow>Ihre Ansprechpartner</Eyebrow>
          <h2
            className="mt-3 text-3xl lg:text-4xl tracking-tight text-zeb-tertiary"
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              letterSpacing: '-0.02em'
            }}>
            Sprechen Sie uns an.
          </h2>
          <p className="mt-3 max-w-2xl text-body-sm text-zeb-gray-600">
            Unser zeb-Team rund um Kundenzentrierung bei Finanzdienstleistern begleitet Sie vom
            Reifegrad über die Roadmap bis zur wirkungsvollen Umsetzung. Im Erstgespräch ordnen wir
            Ihren Score ein und zeigen die wirkungsvollsten Hebel.
          </p>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {CONTACTS.map((c) => (
              <div
                key={c.name}
                className="rounded-lg border border-zeb-muted bg-white p-5 text-center shadow-zeb-xs">
                <div className="mx-auto h-16 w-16 overflow-hidden rounded-full bg-zeb-surface ring-1 ring-zeb-muted">
                  <ContactAvatar src={c.image} name={c.name} />
                </div>
                <div
                  className="mt-4 text-note leading-tight text-zeb-tertiary"
                  style={{
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 700,
                    letterSpacing: '-0.02em'
                  }}>
                  {c.name}
                </div>
                <div className="mt-1 text-meta text-zeb-gray-500">{c.role}</div>
                {c.email && (
                  <a
                    href={`mailto:${c.email.toLowerCase()}`}
                    className="mt-1.5 inline-block text-meta text-zeb-primary hover:underline break-all">
                    {c.email.toLowerCase()}
                  </a>
                )}
              </div>
            ))}
          </div>
        </motion.section>
      </main>
    </PageMotion>
  );
}
